import React, { useEffect, useState, useRef } from "react";
import FilterPage from "./filter";
import { useNavigate } from "react-router-dom";
import { usePropertySearch, buildPropertySearchParams } from "./api/getCheckProperty";
import LoadingScreen from "../../../loading/loadingScreen";
import SideModal from "../home/sideModal";
import FilterIcon from "../../../assets/images/illustrations/Filter.svg";
import LocationIcon from "../../../assets/images/illustrations/Location.svg";

const FirstPageProperties = () => {
 const [loading, setLoading] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const placesService = useRef(null);
  const [placeholder, setPlaceholder] = useState("Enter city");
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);

  const autocompleteService = useRef(null);
  const inputRef = useRef(null);

  const [filters, setFilters] = useState({
    min: 0,
    max: 5000001,
    sqftMin: 0,
    sqftMax: 15001,
    bedrooms: null,
    bathrooms: null,
    property: null,
    city: "",
    state: "",
    country: "",
    street: "",
    streetNumber: "",
    postalCode: "",
    searchCity: "",
    selectedOption: "Buy"
  });

  const navigate = useNavigate();
  const { checkProperty } = usePropertySearch();
  const extractAddressComponents = (address) => {
  const getLong = (type) =>
    address.address_components?.find(c => c.types.includes(type))?.long_name || "";

  const getShort = (type) =>
    address.address_components?.find(c => c.types.includes(type))?.short_name || "";

  return {
    streetNumber: getLong("street_number"),
    street: getLong("route"),
    city: getLong("locality") || getLong("sublocality") || "",
    state: getShort("administrative_area_level_1"),
    country: getShort("country"),
    postalCode: getLong("postal_code"),
  };
};

const parseWithGoogle = (searchText) => {
  return new Promise((resolve) => {
    if (!placesService.current) {
      resolve({ unparsedAddress: searchText });
      return;
    }
    
    // Use Places Text Search instead of Geocoding
    const request = {
      query: searchText,
      fields: ['place_id']
    };
    
    placesService.current.textSearch(request, (results, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK && results[0]) {
        // Get place details to extract address components
        placesService.current.getDetails(
          { placeId: results[0].place_id },
          (place, detailStatus) => {
            if (detailStatus === window.google.maps.places.PlacesServiceStatus.OK && place) {
              const parsed = extractAddressComponents(place);
              resolve(parsed);
            } else {
              resolve({ unparsedAddress: searchText });
            }
          }
        );
      } else {
      
        resolve({ unparsedAddress: searchText });
      }
    });
  });
};

  // --------------------------------------
  // GOOGLE AUTOCOMPLETE SETUP
  // --------------------------------------
  useEffect(() => {
    const updatePlaceholder = () => {
      if (window.innerWidth >= 640) {
        setPlaceholder("Enter city, ZIP code, or neighborhood...");
      } else {
        setPlaceholder("Enter city");
      }
    };

    updatePlaceholder();
    window.addEventListener("resize", updatePlaceholder);

    const initializeGoogleServices = () => {
      if (window.google) {
        autocompleteService.current = new window.google.maps.places.AutocompleteService();
        placesService.current = new window.google.maps.places.PlacesService(document.createElement("div"));
      }
    };

    if (!window.google) {
      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
      script.async = true;
      script.onload = initializeGoogleServices;
      document.head.appendChild(script);
    } else {
      initializeGoogleServices();
    }

    return () => window.removeEventListener("resize", updatePlaceholder);
  }, []);

  // --------------------------------------
  // FILTER SAVE HANDLER
  // --------------------------------------
 const handleFilterSave = async (values) => {

  const loc = {
    city: filters.city,
    state: filters.state,
    country: filters.country,
  };

  const finalFilters = {
    ...filters,
    ...values,
    city: loc.city,
    state: loc.state,
    country: loc.country,
    listingType: filters.selectedOption,
  };

  setFilters(finalFilters);
  setFilterOpen(false);

  if (!filters.searchCity) {
    return;
  }

  try {

    setLoading("loading");

    const data = await checkProperty(finalFilters);

    if (!data) {
      setLoading("error");
      return;
    }

    const properties = data.value || data;

    if (!properties || properties.length === 0) {
      setLoading("empty");
      return;
    }

        setLoading(null);

    navigate(`/details/properties?${buildPropertySearchParams(finalFilters).toString()}`, {
      state: {
        data,
        filters: finalFilters,
      },
    });

  } catch (err) {

    console.error(err);
    setLoading("error");

  }

};

  const handleSearch = async () => {
  if (!filters.searchCity) {
    alert("Please enter a city");
    return;
  }

  try {

    let finalFilters;

    if (!filters.city) {

      const parsed = await parseWithGoogle(filters.searchCity);

      finalFilters = {
        ...filters,
        ...parsed,
        listingType: filters.selectedOption,
      };

    } else {

      finalFilters = {
        ...filters,
        listingType: filters.selectedOption,
      };

    }

    setLoading("loading");

    const data = await checkProperty(finalFilters);

    if (!data) {
      setLoading("error");
      return;
    }

    const properties = data.value || data;

    if (!properties || properties.length === 0) {
      setLoading("empty");
      return;
    }

    setLoading(null);

    navigate(`/details/properties?${buildPropertySearchParams(finalFilters).toString()}`, {
      state: {
        data,
        filters: finalFilters,
      },
    });

  } catch (err) {

    console.error(err);
    setLoading("error");

  }

};


const handleSuggestionSelect = (suggestion) => {
  if (!placesService.current) return;

  placesService.current.getDetails(
    { placeId: suggestion.place_id },
    (place) => {
      if (!place) return;

      const parsed = extractAddressComponents(place);

      setFilters((prev) => ({
        ...prev,
        searchCity: suggestion.description,
        city: parsed.city,
        state: parsed.state,
        country: parsed.country,
        street: parsed.street,
        streetNumber: parsed.streetNumber,
        postalCode: parsed.postalCode,
      }));

      setSuggestions([]);
      setShowDropdown(false);

    
inputRef.current?.focus();
    }
  );
};

const filterCount =
  Number(!!filters.bedrooms) +
  Number(!!filters.bathrooms) +
  Number(!!filters.property) +
  Number(filters.min > 0 || filters.max < 5000001) +
  Number(filters.sqftMin > 0 || filters.sqftMax < 15001);


  return (
    <div>
    {loading && (
  <LoadingScreen
    location={filters.searchCity}
    status={loading}
    onClose={() => setLoading(null)}
  />
)}

      <div
  className="
    relative
    z-10
    max-w-7xl
    mx-auto
    px-5
    lg:px-8
    py-10
    md:py-16
    lg:py-20
    font-Montserrat
">
                
        {/* Heading */}
      <h1 className="text-[30px] md:text-6xl leading-tight font-bold text-white text-center max-w-3xl mx-auto">
  Find Your{" "}
  <span className="font-playfair italic font-normal text-[#A61E22]">
    Next Home
  </span>
</h1>

<p className="mt-4 text-[15px] md:text-lg leading-6 md:leading-7 text-white/80 max-w-2xl mx-auto text-center">
  Search by city, neighborhood, ZIP code, or address across Central Ohio's
  latest MLS listings.
</p>
       
   <div className="max-w-3xl mx-auto mt-8 md:mt-12">

 <div className="bg-white/15 backdrop-blur-2xl border border-white/20 rounded-[28px] p-7 shadow-[0_25px_60px_rgba(0,0,0,0.25)]">
    {/* Buy / Rent */}
<div className="flex justify-center mb-6">

  <div className="relative inline-flex bg-white rounded-full p-0.5 shadow-xl">

    {/* Sliding Pill */}
    <div
      className={`absolute top-1 bottom-1 rounded-full bg-[#A61E22] shadow-md transition-transform duration-300 ease-in-out
      w-[calc(50%-4px)]
      ${
        filters.selectedOption === "Buy"
          ? "translate-x-0"
          : "translate-x-full"
      }`}
      style={{ left: "4px" }}
    />

    <button
      onClick={() =>
        setFilters((prev) => ({
          ...prev,
          selectedOption: "Buy",
        }))
      }
      className={`relative z-10
        w-28 md:w-32
        py-2 md:py-2.5
        rounded-full
        text-base md:text-lg
        font-medium
        transition-colors duration-300
        ${
          filters.selectedOption === "Buy"
            ? "text-white"
            : "text-gray-600"
        }`}
    >
      Buy
    </button>

    <button
      onClick={() =>
        setFilters((prev) => ({
          ...prev,
          selectedOption: "Rent",
        }))
      }
      className={`relative z-10
        w-28 md:w-32
        py-2 md:py-2.5
        rounded-full
        text-base md:text-lg
        font-medium
        transition-colors duration-300
        ${
          filters.selectedOption === "Rent"
            ? "text-white"
            : "text-gray-600"
        }`}
    >
      Rent
    </button>

  </div>

</div>
{/* Search Input */}
<div className="relative w-full">

  {/* Filter Button */}
<button
  type="button"
  onClick={() => setFilterOpen(true)}
  aria-label="Open filters"
  className="
    absolute
    left-2
    md:left-3
    top-1/2
    -translate-y-1/2
    z-20
    w-10
    h-10
    rounded-full
    flex
    items-center
    justify-center
    hover:bg-black/5
    transition-all
    duration-200
  "
>

  <div className="relative">

    <img
      src={FilterIcon}
      alt="Filters"
      className={`
        w-5
        h-5
        md:w-6
        md:h-6
        transition-all
        duration-200
        ${
          filterCount > 0
            ? "opacity-100 scale-105"
            : "opacity-70"
        }
      `}
    />

    {filterCount > 0 && (
      <span
        className="
          absolute
          -top-2
          -right-2
          min-w-[18px]
          h-[18px]
          px-1
          rounded-full
          bg-[#A61E22]
          text-white
          text-[10px]
          font-bold
          leading-none
          flex
          items-center
          justify-center
          shadow-md
          ring-2
          ring-white
        "
      >
        {filterCount}
      </span>
    )}

  </div>

</button>

  <input
  ref={inputRef}
  className="
    w-full
    h-12 md:h-16
    bg-white/95
    backdrop-blur-md
    rounded-[20px]
    pl-14 md:pl-16
   pr-16 md:pr-20
    text-base md:text-lg
    text-gray-900
    placeholder:text-base md:placeholder:text-lg
    placeholder:text-gray-400
    border border-white/30
    shadow-2xl
    focus:outline-none
    focus:ring-2
    focus:ring-[#A61E22]
    transition-all
    duration-300
  "
  placeholder={placeholder}
  value={filters.searchCity}
  onChange={(e) => {
    const value = e.target.value;
    setFilters({ ...filters, searchCity: value });

    if (value.length > 2 && autocompleteService.current) {
      autocompleteService.current.getPlacePredictions(
        {
          input: value,
          componentRestrictions: { country: "us" },
        },
        (predictions, status) => {
          if (
            status === window.google.maps.places.PlacesServiceStatus.OK &&
            predictions
          ) {
            setSuggestions(predictions);
            setShowDropdown(true);
          } else {
            setSuggestions([]);
            setShowDropdown(false);
          }
        }
      );
    } else {
      setSuggestions([]);
      setShowDropdown(false);
    }
  }}
  onKeyDown={(e) => {
  if (e.key === "Enter") {
    e.preventDefault();

    // If suggestions are visible, automatically use the first one
    if (showDropdown && suggestions.length > 0) {
      handleSuggestionSelect(suggestions[0]);
    } else {
      handleSearch();
    }
  }
}}
  onBlur={() => setTimeout(() => setShowDropdown(false), 100)}
  onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
/>

      {/* Suggestions Dropdown */}
{showDropdown && suggestions.length > 0 && (
  <div
    className="
absolute
top-full
left-0
right-0
mt-2
bg-white
rounded-2xl
border
border-gray-200
shadow-xl
z-50
max-h-80
overflow-y-auto
"
  >
    {suggestions.map((s) => {

      const getLocationType = (types) => {
        if (types?.includes("locality")) return "City";
        if (types?.includes("administrative_area_level_2")) return "County";
        if (types?.includes("administrative_area_level_1")) return "State";
        if (types?.includes("neighborhood")) return "Neighborhood";
        if (types?.includes("sublocality")) return "Area";
        if (types?.includes("postal_code")) return "ZIP Code";
        if (types?.includes("route")) return "Street";
        return "Location";
      };

      return (

     <button
  key={s.place_id}
  type="button"
  onMouseDown={() => handleSuggestionSelect(s)}
  className="
    flex
    items-center
    w-full
    px-4
    py-3
    text-left
    hover:bg-gray-50
    transition-colors
    duration-150
    border-b
    border-gray-100
    last:border-0
  "
>
  {/* Map Icon */}
  <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center mr-3">
    <svg
      className="w-4 h-4 text-gray-600"
      fill="currentColor"
      viewBox="0 0 20 20"
    >
      <path
        fillRule="evenodd"
        d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
        clipRule="evenodd"
      />
    </svg>
  </div>

  {/* Location Details */}
  <div className="flex-1 min-w-0">
    <div className="text-sm font-medium text-gray-900 truncate">
      {s.description}
    </div>

    <div className="text-xs text-gray-500">
      {getLocationType(s.types || [])}
    </div>
  </div>
</button>
      );

    })}
  </div>
)}

      {/* Buttons */}

            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-2">

  
  {/* Search Button */}
<button
  onClick={handleSearch}
  className="
    h-9
    md:h-12
    px-4
    md:px-5
    bg-[#A61E22]
    hover:bg-[#8d181b]
    text-white
   rounded-2xl
    shadow-lg
    transition-all
    duration-300
    hover:scale-[1.02]
    flex
    items-center
    justify-center
  "
  aria-label="Search"
>
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="w-5 h-5 md:w-6 md:h-6"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M21 21l-5.2-5.2m2.2-5.3a7 7 0 11-14 0 7 7 0 0114 0z"
    />
  </svg>
</button>

            </div>
      </div>

    </div>

  </div>


</div>

      <SideModal />

      {filterOpen && (
        <FilterPage
          close={() => setFilterOpen(false)}
          onSave={handleFilterSave}
          filterVal={filters}
        />
      )}
    </div>
  );
};

export default FirstPageProperties;